/**
 * 資料セクションテンプレートの CRUD hooks。
 * Firestore: `documentSectionTemplates/{id}`
 * - 組織共有（admin 全員が利用可能）
 * - editor 以上が書き込み、viewer は read のみ
 */
import type {
  DocumentSection,
  DocumentSectionTemplate,
  DocumentSectionType,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';

import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/firebase';

export interface DocumentSectionTemplateRecord extends DocumentSectionTemplate {
  /** UI 表示用の作成日時 (ms epoch)。Firestore Timestamp から変換。 */
  createdAtMs?: number;
}

const COLLECTION = 'documentSectionTemplates';

export function useDocumentSectionTemplates() {
  return useQuery({
    queryKey: ['documentSectionTemplates'],
    queryFn: async (): Promise<DocumentSectionTemplateRecord[]> => {
      const snap = await getDocs(
        query(collection(db, COLLECTION), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => {
        const data = d.data() as DocumentSectionTemplate;
        const ts = data.createdAt as Timestamp | undefined;
        return {
          ...data,
          id: d.id,
          createdAtMs: ts && typeof ts.toMillis === 'function' ? ts.toMillis() : undefined,
        };
      });
    },
  });
}

interface SaveArgs {
  name: string;
  description?: string;
  section: DocumentSection;
}

export function useSaveDocumentSectionTemplate() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ name, description, section }: SaveArgs) => {
      if (!user?.uid) throw new Error('未ログインです');
      const payload = {
        name: name.trim(),
        description: description?.trim() || null,
        type: section.type as DocumentSectionType,
        // variant は undefined の可能性があるため明示的に null/string で保存
        variant: section.variant ?? null,
        // config は構造ごとシリアライズして保存
        config: JSON.parse(JSON.stringify(section.config ?? {})),
        createdBy: user.uid,
        createdByName: user.displayName ?? user.email ?? null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, COLLECTION), payload);
      return { id: ref.id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentSectionTemplates'] });
    },
  });
}

export function useDeleteDocumentSectionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, COLLECTION, id));
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentSectionTemplates'] });
    },
  });
}
